/* eslint-disable @typescript-eslint/unbound-method */
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { logout, loginFailed } from "@/store/authSlice";
import Logger from "@/utils/Logger";

import { AuthService } from "@/service/AuthService";

export const useAuthActions = () => {
  const dispatch = useDispatch();
  const oidcAuth = useOidcAuth();

  const signIn = useCallback(async (): Promise<void> => {
    Logger.info("Sign in action initiated", ["useAuthActions"]);
    try {
      await AuthService.performSignIn(oidcAuth.signinRedirect);
      await AuthService.performClearStaleState(oidcAuth.clearStaleState);
      Logger.info("Sign in action completed successfully", ["useAuthActions"]);
    } catch (err) {
      Logger.error("Sign in action failed", err, ["useAuthActions"]);
      dispatch(loginFailed("Sign in failed"));
      throw err;
    }
  }, [oidcAuth, dispatch]);

  const signOut = useCallback(async (): Promise<void> => {
    Logger.info("Sign out action initiated", ["useAuthActions"]);
    try {
      await AuthService.performSignOut(oidcAuth.signoutRedirect);
      dispatch(logout());
      Logger.info("Sign out action completed successfully", ["useAuthActions"]);
    } catch (err) {
      Logger.error("Sign out action failed", err, ["useAuthActions"]);
      dispatch(logout());
      throw err;
    }
  }, [oidcAuth, dispatch]);

  const signInSilent = useCallback(async (): Promise<void> => {
    Logger.info("Silent sign in action initiated", ["useAuthActions"]);
    try {
      await AuthService.performSilentSignIn(oidcAuth.signinSilent);
      Logger.info("Silent sign in action completed successfully", [
        "useAuthActions",
      ]);
    } catch (err) {
      Logger.error("Silent sign in action failed", err, ["useAuthActions"]);
      dispatch(loginFailed("Silent authentication failed"));
      throw err;
    }
  }, [oidcAuth, dispatch]);

  const handleUnauthorized = useCallback(() => {
    Logger.warn("Unauthorized access detected - attempting silent renewal", [
      "useAuthActions",
    ]);
    if (oidcAuth.user && !oidcAuth.user.expired) {
      Logger.info("Attempting silent token renewal after 401 response", [
        "useAuthActions",
      ]);
      oidcAuth.signinSilent().catch((err) => {
        Logger.error("Silent renewal failed after 401", err, [
          "useAuthActions",
        ]);
      });
    } else {
      Logger.warn("Cannot attempt silent renewal - no valid user session", [
        "useAuthActions",
      ]);
    }
  }, [oidcAuth]);

  return {
    signIn,
    signOut,
    signInSilent,
    handleUnauthorized,
  };
};
